import React, { FormEvent, useEffect, useMemo, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import {
  deleteProject,
  getProjects,
  PortfolioProject,
  saveProject,
} from "@/services/projects";

const emptyProject: PortfolioProject = {
  title: "",
  description: "",
  tags: [],
  imageUrl: "",
  liveUrl: "",
  githubUrl: "",
  status: "Live",
  featured: false,
  order: 0,
};

const AdminProjectsPage: React.FC = () => {
  const { accessKey } = useParams();
  const expectedKey = import.meta.env.VITE_ADMIN_ROUTE_KEY as string | undefined;
  const routeAllowed = Boolean(expectedKey && accessKey === expectedKey);

  const [projects, setProjects] = useState<PortfolioProject[]>([]);
  const [form, setForm] = useState<PortfolioProject>(emptyProject);
  const [tagsText, setTagsText] = useState("");
  const [adminKey, setAdminKey] = useState(
    () => localStorage.getItem("portfolio-admin-key") || "",
  );
  const [message, setMessage] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const sortedProjects = useMemo(
    () =>
      [...projects].sort(
        (a, b) => Number(a.order || 0) - Number(b.order || 0),
      ),
    [projects],
  );

  useEffect(() => {
    if (!routeAllowed) return;
    getProjects().then(setProjects);
  }, [routeAllowed]);

  if (!routeAllowed) {
    return <Navigate to="/" replace />;
  }

  const resetForm = () => {
    setForm(emptyProject);
    setTagsText("");
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setIsSaving(true);
    setMessage("");

    try {
      localStorage.setItem("portfolio-admin-key", adminKey);
      await saveProject(
        {
          ...form,
          tags: tagsText
            .split(",")
            .map((tag) => tag.trim())
            .filter(Boolean),
        },
        adminKey,
      );
      setProjects(await getProjects());
      resetForm();
      setMessage("Project saved.");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Save failed.");
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (project: PortfolioProject) => {
    setForm(project);
    setTagsText(project.tags.join(", "));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDelete = async (project: PortfolioProject) => {
    if (!project.id) {
      setMessage("This project is missing a database id.");
      return;
    }

    setMessage("");
    await deleteProject(project.id, adminKey);
    setProjects(await getProjects());
    setMessage("Project deleted.");
  };

  return (
    <main className="min-h-screen bg-[#f6f4ef] px-6 py-10 text-[#181818] md:px-12 lg:px-24">
      <section className="mx-auto max-w-6xl">
        <div className="mb-10 flex flex-wrap items-end justify-between gap-6">
          <div>
            <p className="text-xs uppercase tracking-[0.28em] text-black/45">
              Private Dashboard
            </p>
            <h1
              className="mt-3 uppercase leading-none"
              style={{
                fontFamily: "'League Gothic', sans-serif",
                fontSize: "clamp(3.5rem, 10vw, 8rem)",
              }}
            >
              Projects
            </h1>
          </div>
          <div className="flex flex-wrap gap-3">
            <a
              href={`/manage/all-dbs/${accessKey}`}
              className="rounded-full border border-black/20 px-5 py-3 text-xs uppercase tracking-[0.2em] transition hover:bg-white"
            >
              All DBs
            </a>
            <a
              href="/"
              className="rounded-full border border-black/20 px-5 py-3 text-xs uppercase tracking-[0.2em] transition hover:bg-black hover:text-white"
            >
              Portfolio
            </a>
          </div>
        </div>

        <form
          onSubmit={handleSubmit}
          className="grid gap-5 border-y border-black/15 py-8 md:grid-cols-2"
        >
          <label className="grid gap-2 text-sm font-medium">
            Admin API Key
            <input
              value={adminKey}
              onChange={(event) => setAdminKey(event.target.value)}
              className="border border-black/15 bg-white px-4 py-3 outline-none focus:border-black"
              type="password"
              required
            />
          </label>
          <label className="grid gap-2 text-sm font-medium">
            Title
            <input
              value={form.title}
              onChange={(event) =>
                setForm((current) => ({ ...current, title: event.target.value }))
              }
              className="border border-black/15 bg-white px-4 py-3 outline-none focus:border-black"
              required
            />
          </label>
          <label className="grid gap-2 text-sm font-medium md:col-span-2">
            Description
            <textarea
              value={form.description}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  description: event.target.value,
                }))
              }
              className="min-h-28 border border-black/15 bg-white px-4 py-3 outline-none focus:border-black"
              required
            />
          </label>
          <label className="grid gap-2 text-sm font-medium">
            Tags
            <input
              value={tagsText}
              onChange={(event) => setTagsText(event.target.value)}
              className="border border-black/15 bg-white px-4 py-3 outline-none focus:border-black"
              placeholder="React, Wix, TypeScript"
            />
          </label>
          <label className="grid gap-2 text-sm font-medium">
            Image URL
            <input
              value={form.imageUrl}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  imageUrl: event.target.value,
                }))
              }
              className="border border-black/15 bg-white px-4 py-3 outline-none focus:border-black"
              required
            />
          </label>
          <label className="grid gap-2 text-sm font-medium">
            Live URL
            <input
              value={form.liveUrl || ""}
              onChange={(event) =>
                setForm((current) => ({ ...current, liveUrl: event.target.value }))
              }
              className="border border-black/15 bg-white px-4 py-3 outline-none focus:border-black"
            />
          </label>
          <label className="grid gap-2 text-sm font-medium">
            GitHub URL
            <input
              value={form.githubUrl || ""}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  githubUrl: event.target.value,
                }))
              }
              className="border border-black/15 bg-white px-4 py-3 outline-none focus:border-black"
            />
          </label>
          <label className="grid gap-2 text-sm font-medium">
            Status
            <input
              value={form.status || ""}
              onChange={(event) =>
                setForm((current) => ({ ...current, status: event.target.value }))
              }
              className="border border-black/15 bg-white px-4 py-3 outline-none focus:border-black"
            />
          </label>
          <label className="grid gap-2 text-sm font-medium">
            Order
            <input
              value={form.order || 0}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  order: Number(event.target.value),
                }))
              }
              className="border border-black/15 bg-white px-4 py-3 outline-none focus:border-black"
              type="number"
            />
          </label>
          <label className="flex items-center gap-3 text-sm font-medium">
            <input
              checked={Boolean(form.featured)}
              onChange={(event) =>
                setForm((current) => ({
                  ...current,
                  featured: event.target.checked,
                }))
              }
              type="checkbox"
            />
            Featured
          </label>
          <div className="flex flex-wrap items-center gap-3 md:col-span-2">
            <button
              disabled={isSaving}
              className="rounded-full bg-black px-6 py-3 text-xs uppercase tracking-[0.2em] text-white transition hover:bg-black/80 disabled:opacity-50"
            >
              {isSaving ? "Saving" : form.id ? "Update Project" : "Add Project"}
            </button>
            <button
              type="button"
              onClick={resetForm}
              className="rounded-full border border-black/20 px-6 py-3 text-xs uppercase tracking-[0.2em] transition hover:bg-white"
            >
              Clear
            </button>
            {message && <p className="text-sm text-black/60">{message}</p>}
          </div>
        </form>

        <section className="mt-10 grid gap-4">
          {sortedProjects.map((project) => (
            <article
              key={project.id || project.title}
              className="grid gap-5 border border-black/10 bg-white p-5 md:grid-cols-[180px_1fr_auto]"
            >
              <img
                src={project.imageUrl}
                alt={project.title}
                className="h-36 w-full object-cover md:h-full"
              />
              <div>
                <p className="text-xs uppercase tracking-[0.22em] text-black/45">
                  {project.status || "Project"}
                </p>
                <h2 className="mt-2 text-2xl font-semibold">{project.title}</h2>
                <p className="mt-3 max-w-3xl text-sm leading-6 text-black/60">
                  {project.description}
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  {project.tags.map((tag) => (
                    <span
                      key={tag}
                      className="rounded-full border border-black/15 px-3 py-1 text-[11px] uppercase tracking-[0.15em]"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
              <div className="flex gap-3 md:flex-col">
                <button
                  onClick={() => handleEdit(project)}
                  className="rounded-full border border-black/20 px-4 py-2 text-xs uppercase tracking-[0.18em]"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(project)}
                  className="rounded-full border border-black/20 px-4 py-2 text-xs uppercase tracking-[0.18em]"
                >
                  Delete
                </button>
              </div>
            </article>
          ))}
          {!sortedProjects.length && (
            <div className="border border-black/10 bg-white p-8 text-sm leading-7 text-black/60">
              No projects found in MongoDB yet. Add your first project from the
              form above.
            </div>
          )}
        </section>
      </section>
    </main>
  );
};

export default AdminProjectsPage;
